import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { User, Organization } from '@/types';
import { isDemoMode } from '@/lib/supabase';
import * as authService from '@/services/supabase/auth';

interface AuthState {
  user: User | null;
  organization: Organization | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;

  // Actions
  setUser: (user: User | null) => void;
  setOrganization: (organization: Organization | null) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  updateUser: (updates: Partial<User>) => void;
  signUp: (data: {
    email: string;
    password: string;
    full_name: string;
    organization_name: string;
    property_type?: string;
  }) => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  initialize: () => Promise<void>;
}

// Demo user for development
const demoUser: User = {
  id: 'demo-user-1',
  email: 'demo@axis.app',
  full_name: 'Demo User',
  phone: '+1 (555) 123-4567',
  avatar_url: undefined,
  organization_id: 'demo-org-1',
  role: 'manager',
  assigned_properties: ['demo-property-1'],
  department: 'facilities',
  status: 'active',
  notification_prefs: { email: true, push: true, sms: false },
  created_at: new Date().toISOString(),
  updated_at: new Date().toISOString(),
};

const demoOrganization: Organization = {
  id: 'demo-org-1',
  name: 'Sunny Property Management',
  slug: 'sunny-pm',
  logo_url: undefined,
  property_type: 'apartment',
  timezone: 'America/New_York',
  date_format: 'MM/DD/YYYY',
  currency: 'USD',
  settings: {
    terminology: {
      unit: 'unit',
      occupant: 'resident',
    },
    features: {
      guestReporting: false,
      vendorPortal: true,
      workOrders: true,
      assetTracking: true,
    },
    branding: {
      primaryColor: '#2563eb',
    },
    notifications: {
      emailEnabled: true,
      smsEnabled: false,
      pushEnabled: true,
    },
  },
  subscription_tier: 'professional',
  subscription_status: 'active',
  created_at: new Date().toISOString(),
  updated_at: new Date().toISOString(),
};

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      organization: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,

      setUser: (user) => set({ user, isAuthenticated: !!user }),

      setOrganization: (organization) => set({ organization }),

      setLoading: (isLoading) => set({ isLoading }),

      setError: (error) => set({ error }),

      login: async (email: string, password: string) => {
        set({ isLoading: true, error: null });

        try {
          // Demo credentials always use the demo user (works with or without Supabase)
          if (email === 'demo@axis.app' && password === 'demo123') {
            await new Promise((resolve) => setTimeout(resolve, 500));
            set({
              user: demoUser,
              organization: demoOrganization,
              isAuthenticated: true,
              isLoading: false,
            });
            return;
          }

          // In demo mode (no Supabase configured), accept any credentials
          if (isDemoMode()) {
            await new Promise((resolve) => setTimeout(resolve, 500));
            set({
              user: { ...demoUser, email, full_name: email.split('@')[0] },
              organization: demoOrganization,
              isAuthenticated: true,
              isLoading: false,
            });
            return;
          }

          // Real Supabase authentication
          const { profile, organization } = await authService.signIn({
            email,
            password,
          });

          if (!profile || !organization) {
            throw new Error('Failed to load user profile');
          }

          // Convert database types to app types (null -> undefined)
          const user: User = {
            id: profile.id,
            email: profile.email,
            full_name: profile.full_name,
            phone: profile.phone ?? undefined,
            avatar_url: profile.avatar_url ?? undefined,
            organization_id: profile.organization_id,
            role: profile.role as User['role'],
            assigned_properties: profile.assigned_properties ?? undefined,
            department: profile.department ?? undefined,
            status: profile.status as User['status'],
            notification_prefs: (profile.notification_prefs as unknown) as User['notification_prefs'],
            created_at: profile.created_at,
            updated_at: profile.updated_at,
          };

          const org: Organization = {
            id: organization.id,
            name: organization.name,
            slug: organization.slug,
            logo_url: organization.logo_url ?? undefined,
            property_type: organization.property_type as Organization['property_type'],
            timezone: organization.timezone,
            date_format: organization.date_format,
            currency: organization.currency,
            settings: organization.settings as Organization['settings'],
            subscription_tier: organization.subscription_tier as Organization['subscription_tier'],
            subscription_status: organization.subscription_status as Organization['subscription_status'],
            created_at: organization.created_at,
            updated_at: organization.updated_at,
          };

          set({
            user,
            organization: org,
            isAuthenticated: true,
            isLoading: false,
          });
        } catch (error) {
          set({
            error: error instanceof Error ? error.message : 'Login failed',
            isLoading: false,
          });
          throw error;
        }
      },

      logout: async () => {
        try {
          if (!isDemoMode()) {
            await authService.signOut();
          }
        } catch (error) {
          console.error('Logout error:', error);
        } finally {
          // Clear demo session cookie
          if (typeof document !== 'undefined') {
            document.cookie = 'axis-demo-session=; path=/; max-age=0; SameSite=Lax';
          }
          set({
            user: null,
            organization: null,
            isAuthenticated: false,
            error: null,
          });
        }
      },

      updateUser: (updates) =>
        set((state) => ({
          user: state.user ? { ...state.user, ...updates } : null,
        })),

      signUp: async (data) => {
        set({ isLoading: true, error: null });

        try {
          if (isDemoMode()) {
            throw new Error('Sign up is not available in demo mode. Please configure Supabase credentials.');
          }

          const result = await authService.signUp(data);

          // Check if email confirmation is required
          if (result.requiresEmailConfirmation) {
            set({
              isLoading: false,
              error: 'Please check your email to confirm your account before logging in.',
            });
            return; // Don't try to login - user needs to confirm email first
          }

          // Email confirmation not required - log them in
          set({
            isLoading: false,
            error: null,
          });

          await get().login(data.email, data.password);
        } catch (error) {
          set({
            error: error instanceof Error ? error.message : 'Sign up failed',
            isLoading: false,
          });
          throw error;
        }
      },

      resetPassword: async (email: string) => {
        set({ isLoading: true, error: null });

        try {
          if (isDemoMode()) {
            throw new Error('Password reset is not available in demo mode.');
          }

          await authService.resetPassword(email);
          set({ isLoading: false });
        } catch (error) {
          set({
            error: error instanceof Error ? error.message : 'Password reset failed',
            isLoading: false,
          });
          throw error;
        }
      },

      initialize: async () => {
        // Check for existing session on app load
        if (isDemoMode()) {
          // In demo mode, restore from persisted state
          return;
        }

        set({ isLoading: true });

        try {
          const session = await authService.getSession();

          if (session?.user) {
            const profile = await authService.getUserProfile(session.user.id);
            const organization = profile
              ? await authService.getOrganization(profile.organization_id)
              : null;

            if (profile && organization) {
              const user: User = {
                id: profile.id,
                email: profile.email,
                full_name: profile.full_name,
                phone: profile.phone ?? undefined,
                avatar_url: profile.avatar_url ?? undefined,
                organization_id: profile.organization_id,
                role: profile.role as User['role'],
                assigned_properties: profile.assigned_properties ?? undefined,
                department: profile.department ?? undefined,
                status: profile.status as User['status'],
                notification_prefs: (profile.notification_prefs as unknown) as User['notification_prefs'],
                created_at: profile.created_at,
                updated_at: profile.updated_at,
              };

              const org: Organization = {
                id: organization.id,
                name: organization.name,
                slug: organization.slug,
                logo_url: organization.logo_url ?? undefined,
                property_type: organization.property_type as Organization['property_type'],
                timezone: organization.timezone,
                date_format: organization.date_format,
                currency: organization.currency,
                settings: organization.settings as Organization['settings'],
                subscription_tier: organization.subscription_tier as Organization['subscription_tier'],
                subscription_status: organization.subscription_status as Organization['subscription_status'],
                created_at: organization.created_at,
                updated_at: organization.updated_at,
              };

              set({
                user,
                organization: org,
                isAuthenticated: true,
                isLoading: false,
              });
              return;
            }
          }

          set({
            user: null,
            organization: null,
            isAuthenticated: false,
            isLoading: false,
          });
        } catch (error) {
          console.error('Auth initialization error:', error);
          set({
            user: null,
            organization: null,
            isAuthenticated: false,
            isLoading: false,
          });
        }
      },
    }),
    {
      name: 'axis-auth',
      partialize: (state) => ({
        user: state.user,
        organization: state.organization,
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
);

// Initialize auth state listener for Supabase
if (!isDemoMode()) {
  authService.onAuthStateChange((event) => {
    if (event === 'SIGNED_OUT') {
      useAuthStore.getState().logout();
    }
  });
}

// Selector hooks for common patterns
export const useUser = () => useAuthStore((state) => state.user);
export const useOrganization = () => useAuthStore((state) => state.organization);
export const useIsAuthenticated = () => useAuthStore((state) => state.isAuthenticated);
export const useUserRole = () => useAuthStore((state) => state.user?.role);
